'use client';

import { useState } from 'react';
import {
  ChevronDown,
  CircleCheck,
  CircleSlash,
  TriangleAlert,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import { Progress } from '@/components/ui/progress';
import { UPLOAD_ATTENTION_TONE_CLASS } from '@/components/org-chat/pending-upload-send/pending-upload-send-file-row';
import {
  formatFolderUploadPanelCopy as fill,
  type FolderUploadPanelCopy,
} from '@/i18n/folder-upload-panel-copy';
import type { FolderUploadManager } from '@/lib/uploads/folder-upload/manager';
import type { FolderUploadJobState } from '@/lib/uploads/folder-upload/types';
import { cn } from '@/lib/utils';

import { FolderUploadFileList } from './folder-upload-file-list';
import {
  folderUploadJobView,
  folderUploadRows,
  formatFolderUploadCount,
} from './folder-upload-panel-view';

function StatusIcon({ phase }: { phase: string }) {
  if (phase === 'running') return <Loader size="sm" className="shrink-0" />;
  if (phase === 'done')
    return (
      <CircleCheck
        aria-hidden
        className="text-muted-foreground size-4 shrink-0"
      />
    );
  if (phase === 'cancelled')
    return (
      <CircleSlash
        aria-hidden
        className="text-muted-foreground size-4 shrink-0"
      />
    );
  return (
    <TriangleAlert
      aria-hidden
      className={cn('size-4 shrink-0', UPLOAD_ATTENTION_TONE_CLASS)}
    />
  );
}

/**
 * One upload in the panel: what it is doing, overall progress, Cancel all
 * while it runs, and once it stops a summary with the failed files one
 * click away, Retry failed and Dismiss. Stays until dismissed.
 */
export function FolderUploadJobCard({
  job,
  copy,
  locale,
  myDriveLabel,
  manager,
}: {
  job: FolderUploadJobState;
  copy: FolderUploadPanelCopy;
  locale: string;
  myDriveLabel: string;
  manager: Pick<
    FolderUploadManager,
    'cancelAll' | 'cancelFile' | 'retryFailed' | 'retryFile' | 'dismiss'
  >;
}) {
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState<'all' | 'failed'>('all');
  const view = folderUploadJobView(job, {
    copy,
    locale,
    myDriveLabel,
    // The rate is measured up to the latest progress, so time left is
    // derived from state alone.
    now: job.rateSamples.at(-1)?.at ?? job.startedAt,
  });
  const listFilter = view.failedCount > 0 ? filter : 'all';
  const toggle = () => {
    // A finished upload with failures opens on them; the chevron is the
    // visible affordance, the whole header is the target (#5823 TASTE).
    if (!expanded && view.phase !== 'running' && view.failedCount > 0)
      setFilter('failed');
    setExpanded((value) => !value);
  };
  // Dismiss sits in the footer whenever there is one, else in the header.
  const footerDismiss = view.canDismiss && view.canRetryFailed;
  return (
    <section
      aria-label={view.title}
      data-folder-upload-job={view.phase}
      className="bg-popover text-popover-foreground shadow-popover pointer-events-auto flex flex-col gap-2 rounded-2xl p-4"
    >
      <div className="flex items-start gap-2">
        <span className="flex h-5 items-center">
          <StatusIcon phase={view.phase} />
        </span>
        {/* Announced in 10% steps, not on every progress tick. */}
        <span className="sr-only" role="status" aria-live="polite">
          {view.phase === 'running' && !view.cancelling
            ? `${view.title}. ${new Intl.NumberFormat(locale, {
                style: 'percent',
              }).format(Math.floor(view.percent / 10) / 10)}`
            : `${view.title}. ${view.summary}`}
        </span>
        {/* The whole header opens and closes the file list. */}
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          title={expanded ? copy.hideFiles : copy.showFiles}
          className="focus-visible:ring-ring/50 min-w-0 flex-1 rounded-sm text-left outline-none focus-visible:ring-2"
        >
          <span className="type-meta-strong block truncate">{view.title}</span>
          <span className="type-meta text-muted-foreground block text-pretty tabular-nums">
            {view.summary}
          </span>
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground -my-1.5 shrink-0"
          tabIndex={-1}
          aria-hidden
          onClick={toggle}
        >
          <ChevronDown
            aria-hidden
            className={cn(
              'size-4 transition-transform duration-150 ease-out',
              expanded && 'rotate-180'
            )}
          />
        </Button>
        {view.canDismiss && !footerDismiss ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground -my-1.5 -mr-1.5 shrink-0"
            aria-label={copy.dismiss}
            title={copy.dismiss}
            onClick={() => manager.dismiss(job.id)}
          >
            <X aria-hidden className="size-4" />
          </Button>
        ) : null}
      </div>
      {view.phase === 'running' && !view.cancelling ? (
        <Progress
          value={view.percent}
          aria-label={copy.progressLabel}
          className="h-1"
        />
      ) : null}
      {view.emptyFoldersNote ? (
        <p className={cn('type-meta', UPLOAD_ATTENTION_TONE_CLASS)}>
          {view.emptyFoldersNote}
        </p>
      ) : null}
      {expanded ? (
        <>
          {view.failedCount > 0 ? (
            <div
              role="radiogroup"
              aria-label={copy.filterLabel}
              className="bg-muted/60 flex h-8 items-center gap-0.5 self-start rounded-lg p-0.5"
            >
              {(['all', 'failed'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={listFilter === value}
                  onClick={() => setFilter(value)}
                  className={cn(
                    'type-meta flex h-7 items-center rounded-md px-2 tabular-nums',
                    listFilter === value
                      ? 'bg-background text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {value === 'all'
                    ? copy.filterAll
                    : fill(copy.filterFailed, {
                        count: formatFolderUploadCount(
                          view.failedCount,
                          locale
                        ),
                      })}
                </button>
              ))}
            </div>
          ) : null}
          <FolderUploadFileList
            rows={folderUploadRows(job.files, listFilter)}
            copy={copy}
            locale={locale}
            onCancel={(fileId) => manager.cancelFile(job.id, fileId)}
            onRetry={(fileId) => void manager.retryFile(job.id, fileId)}
            // Per-file Retry waits until the upload settles (Cancel all).
            holdRetry={view.phase === 'running'}
          />
        </>
      ) : null}
      {view.canCancelAll || view.canRetryFailed ? (
        <div className="flex items-center gap-2">
          {view.progressNote ? (
            <span className="type-meta text-muted-foreground min-w-0 flex-1 truncate tabular-nums">
              {view.progressNote}
            </span>
          ) : footerDismiss ? (
            <span className="flex-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => manager.dismiss(job.id)}
              >
                {copy.dismiss}
              </Button>
            </span>
          ) : (
            <span className="flex-1" />
          )}
          {view.canCancelAll ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              // Cancelling: disabled in place; the header's spinner is the
              // one busy indicator (TASTE @9fa2ed48).
              disabled={view.cancelling}
              aria-busy={view.cancelling}
              onClick={() => manager.cancelAll(job.id)}
            >
              {copy.cancelAll}
            </Button>
          ) : null}
          {view.canRetryFailed ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void manager.retryFailed(job.id)}
            >
              {view.retryLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
